/**
 * Die Anpassung des Zahlungsplans als Vorschau (Z2.3, `docs/KONZEPT-ZAHLUNGSPLAN.md` Befund B-2).
 *
 * WAS HIER PASSIERT: Aus den Zahlungszeilen, die die Vorgangsseite ohnehin geladen hat, und der
 * Angebotssumme wird ein Bild davon, was der Knopf „Plan anpassen" tun WUERDE — Stufe fuer Stufe
 * alt gegen neu, dazu die Zahl der Zahlungslinks, die dabei sterben, und ein Satz, der beides
 * zusammenfasst. Der Betreiber sieht das VOR dem Klick, nicht danach.
 *
 * WAS HIER NICHT PASSIERT: gerechnet. Kein Betrag wird verteilt, keine Differenz gebildet, kein
 * Anteil bestimmt. Die Verteilung kommt aus `verteileNeu` in `@maestro/pricing` — aus GENAU
 * derselben Funktion, die gleich auch die Route `POST /api/events/:id/payment-plan/adjust`
 * anwendet (Festlegung 6 und 16 im Loop-Dokument). Eine zweite Rechnung an dieser Stelle waere
 * nicht bloss Doppelarbeit: sie faellt erst dann auf, wenn Vorschau und geschriebene Zeilen sich
 * um einen Cent unterscheiden — also beim Kunden, im Beleg, nicht beim Bauen.
 *
 * WARUM KEINE VORSCHAU-ROUTE (Festlegung 18): Beides waere zulaessig gewesen. Die Oberflaeche hat
 * die Zeilen und die Angebotssumme aber bereits im Speicher; eine eigene Route waere ein zweiter
 * Netzaufruf und ein zweiter Ort, der von der ausfuehrenden Route abdriften kann. `apps/web`
 * haengt seit Z1.3 ohnehin an `@maestro/pricing` — dieselbe Datei, dasselbe Ergebnis.
 *
 * DER PREIS DAVON, und er ist bezahlt: Die Vorschau steht auf dem Stand des letzten Ladens. Zahlt
 * ein Kunde in der Zwischenzeit eine Stufe, zeigt sie kurz etwas Veraltetes. Gefaehrlich waere das
 * nur, wenn danach blind geschrieben wuerde — genau dagegen steht die Frische-Pruefung der Route
 * (Festlegung 16): sie vergleicht den ganzen Plan und antwortet `409`, statt auf einem Stand zu
 * schreiben, den es nicht mehr gibt. Die Vorschau darf also irren; die Ausfuehrung nicht.
 *
 * WARUM die Meldungen nicht die des Servers sind: `verteileNeu` liegt in einem Paket ohne Umlaute
 * („laesst sich nicht anpassen"). Das ist im Quelltext richtig und im Fenster eines Betreibers
 * falsch. Uebernommen wird der FEHLERCODE, formuliert wird hier — dieselbe Arbeitsteilung wie in
 * `zahlungsstaffel-anzeige.ts` und `zahlungsdeckung-anzeige.ts`.
 */
import { verteileNeu } from "@maestro/pricing";
import { PAYMENT_TYPE_LABEL, money } from "../ui/format";

/** Die Felder einer Zahlungszeile, die fuer die Vorschau zaehlen — mehr wird nicht gelesen. */
export interface AnpassungsZeile {
  id: string;
  paymentType: string;
  status: string;
  amountCents: number;
  dueDate: string | null;
  /** Gesetzt, sobald ein Zahlungslink erzeugt wurde — dieser Link ueberlebt die Anpassung nicht. */
  checkoutUrl: string | null;
  /** Nur fuer die Reihenfolge gebraucht, siehe `inRoutenReihenfolge`. */
  createdAt: string;
}

/**
 * Dieselbe Reihenfolge, in der die Route rechnet — und das ist kein Schoenheitsfehler.
 *
 * `GET /api/events/:id/payments` liefert die Zeilen absteigend (neueste zuerst, damit die Liste
 * auf der Seite richtig herum steht); die Anpassungs-Route liest sie aufsteigend
 * (`orderBy(asc(createdAt), asc(id))`). `verteileNeu` verteilt den Rundungsrest nach dem
 * Verfahren des groessten Restes und gibt ihn bei Gleichstand an die VORDERE Stufe — bei zwei
 * gleich grossen offenen Stufen bekaeme die Vorschau den Cent also an der anderen Stelle als die
 * Route. Ein Cent Unterschied zwischen dem, was angekuendigt war, und dem, was hinterher
 * dasteht, ist genau die Sorte Abweichung, die niemand mehr erklaeren kann. Deshalb wird die
 * Liste hier in die Reihenfolge der Route gebracht, mit demselben Zweitschluessel: eine Staffel
 * entsteht in EINEM Insert, alle Zeilen tragen denselben `createdAt`.
 */
function inRoutenReihenfolge(zeilen: readonly AnpassungsZeile[]): AnpassungsZeile[] {
  return [...zeilen].sort((a, b) => (a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt < b.createdAt ? -1 : 1));
}

/** Eine Stufe, deren Betrag sich aendern wuerde. Unveraenderte Stufen stehen hier nicht. */
export interface AnpassungsStufe {
  id: string;
  /** „Anzahlung" · „Restzahlung" · … — dieselbe Beschriftung wie in der Zahlungsliste darueber. */
  bezeichnung: string;
  faellig: string | null;
  altCents: number;
  neuCents: number;
  /** Dieser Stufe wird durch die Anpassung ihr versendeter Zahlungslink genommen. */
  linkStirbt: boolean;
}

export interface Anpassungsvorschau {
  /** Darf der ausfuehrende Knopf aktiv sein? Nur wenn es wirklich etwas zu tun gibt. */
  ausfuehrbar: boolean;
  /** Der Plan passt bereits. Kein Fehler — nur kein Anlass (Festlegung 14). */
  unveraendert: boolean;
  /** Ein Eintrag je offener Stufe, deren Betrag sich aendert. */
  stufen: AnpassungsStufe[];
  /** Wie viele bereits versendete Zahlungslinks die Anpassung entwertet. */
  entwerteteLinks: number;
  /** Ein Satz: was passieren wuerde, oder warum es nicht geht. */
  meldung: string;
  /**
   * Die Warnung zu den Zahlungslinks, oder `null`. Sie steht bewusst als EIGENES Feld da und
   * nicht am Ende der Meldung: sie ist die einzige nicht rueckholbare Folge des Klicks und darf
   * in der Oberflaeche hervorgehoben werden, ohne dass jemand einen Satz zerschneiden muss.
   */
  linkwarnung: string | null;
}

/** Der Fehlercode aus `verteileNeu` in einem Satz, der sagt, was der Betreiber stattdessen tun kann. */
function fehlersatz(fehler: string): string {
  switch (fehler) {
    case "keine_angebotssumme":
      return "Ohne Angebotssumme lässt sich der Zahlungsplan nicht anpassen – zuerst eine Angebotsvariante wählen.";
    case "keine_stufen":
      return "Es gibt noch keinen Zahlungsplan, der angepasst werden könnte.";
    case "keine_offene_stufe":
      return "Alle Stufen sind bereits bezahlt oder abgeschlossen – es gibt keine offene Stufe mehr, auf die sich die Differenz verteilen ließe. Hier hilft nur eine zusätzliche Zahlung oder eine Erstattung.";
    case "nichts_zu_verteilen":
      return "Für die offenen Stufen bleibt nichts mehr übrig – es ist bereits mehr gebunden, als das Angebot jetzt fordert. Hier hilft nur Erstatten oder Stornieren, nicht Umverteilen.";
    default:
      return "Der Zahlungsplan lässt sich so nicht anpassen.";
  }
}

/**
 * Was der Knopf tun wuerde — vollstaendig, bevor er gedrueckt wird.
 *
 * Reihenfolge der Faelle ist Absicht: erst „geht nicht" (dann ist alles Weitere gegenstandslos),
 * dann „gibt nichts zu tun", zuletzt der Regelfall. So sagt die Meldung immer das, was als
 * Naechstes zaehlt, und nicht eine Folgewirkung davon.
 */
export function pruefeAnpassung(
  angebotssummeCents: number | null | undefined,
  zeilen: readonly AnpassungsZeile[] | null | undefined,
): Anpassungsvorschau {
  const leer: Anpassungsvorschau = {
    ausfuehrbar: false,
    unveraendert: false,
    stufen: [],
    entwerteteLinks: 0,
    meldung: "",
    linkwarnung: null,
  };
  const liste = inRoutenReihenfolge(zeilen ?? []);

  // Die EINE Rechenstelle. Ihr Urteil wird uebernommen, nicht nachgeprueft.
  const ergebnis = verteileNeu(angebotssummeCents ?? 0, liste);
  if (!ergebnis.ok) return { ...leer, meldung: fehlersatz(ergebnis.fehler) };

  // Nur ECHTE Betragsaenderungen zaehlen — genau die Auswahl, die auch die Route trifft. Eine
  // offene Stufe, die schon richtig steht, ist keine Aenderung und darf in der Vorschau nicht
  // wie eine aussehen (sonst kuendigt die Vorschau eine Wirkung an, die es nicht gibt).
  const stufen: AnpassungsStufe[] = [];
  for (const a of ergebnis.aenderungen) {
    if (a.neuCents === a.altCents) continue;
    const zeile = liste[a.index];
    if (!zeile) continue;
    stufen.push({
      id: zeile.id,
      bezeichnung: PAYMENT_TYPE_LABEL[zeile.paymentType] ?? zeile.paymentType,
      faellig: zeile.dueDate,
      altCents: a.altCents,
      neuCents: a.neuCents,
      linkStirbt: Boolean(zeile.checkoutUrl),
    });
  }

  if (stufen.length === 0) {
    // Festlegung 14: Eine Anpassung ohne Wirkung hat keine Nebenwirkung. Die Route antwortet hier
    // `200 {unveraendert:true}` und fasst keinen Link an — der Knopf darf also nicht so tun, als
    // gaebe es etwas zu tun.
    return { ...leer, unveraendert: true, meldung: "Der Zahlungsplan passt bereits zur Angebotssumme – es gibt nichts anzupassen." };
  }

  const entwerteteLinks = stufen.filter((s) => s.linkStirbt).length;
  const anzahl = stufen.length === 1 ? "Eine offene Stufe wird" : `${stufen.length} offene Stufen werden`;
  const meldung =
    `${anzahl} neu berechnet: die offenen Stufen tragen danach zusammen ${money(ergebnis.offeneSummeNeuCents)} ` +
    `statt ${money(ergebnis.offeneSummeAltCents)}. Bereits bezahlte Stufen bleiben unberührt.`;

  return {
    ausfuehrbar: true,
    unveraendert: false,
    stufen,
    entwerteteLinks,
    meldung,
    // Die einzige nicht rueckholbare Folge des Klicks — und deshalb steht sie VOR ihm. Wer sie
    // erst danach erfaehrt, hat einen Kunden mit einem toten Link und weiss nicht, warum.
    linkwarnung:
      entwerteteLinks === 0
        ? null
        : entwerteteLinks === 1
          ? "Ein bereits versendeter Zahlungslink wird dabei ungültig und muss neu erzeugt und verschickt werden."
          : `${entwerteteLinks} bereits versendete Zahlungslinks werden dabei ungültig und müssen neu erzeugt und verschickt werden.`,
  };
}

/**
 * Was „Plan anpassen“ mit einer Anzahlungsrechnung gemacht hat (Antwortfeld `rechnungskorrektur`
 * der Route, Geld-Zweitpruefung M-3). Die Codes kommen vom Server (auto-storno/auto-invoice), der
 * Satz entsteht hier.
 */
export interface Rechnungskorrektur {
  altPaymentId: string;
  neuPaymentId: string;
  gutschrift: string;
  rechnung: string;
  grund?: string;
  /** Neuer Zahlungslink fuer die ersetzte Anzahlung (nur wenn die alte einen aktiven Link hatte). */
  zahlungslink?: string;
}

export function rechnungskorrekturText(k: Rechnungskorrektur): { ok: boolean; text: string } {
  const r = rechnungsTeil(k);
  if (k.zahlungslink === undefined) return r;
  const link =
    k.zahlungslink === "erstellt"
      ? "Ein neuer Zahlungslink über den neuen Betrag ist erzeugt – er wird nicht automatisch verschickt."
      : `Der neue Zahlungslink konnte nicht erzeugt werden (${k.zahlungslink}) – bitte in der Zahlungsübersicht neu erzeugen.`;
  if (k.rechnung === "nicht_noetig") return { ok: k.zahlungslink === "erstellt", text: link };
  return { ok: r.ok && k.zahlungslink === "erstellt", text: `${r.text} ${link}` };
}

function rechnungsTeil(k: Rechnungskorrektur): { ok: boolean; text: string } {
  if (k.rechnung === "erstellt") {
    return { ok: true, text: "Anzahlungsrechnung ersetzt: die alte Rechnung ist per Gutschrift storniert, die neue über den neuen Betrag liegt zur Freigabe bereit." };
  }
  if (k.rechnung === "angehalten") {
    return { ok: false, text: `Anzahlungsrechnung nicht ersetzt: ${k.grund ?? "ein Prüfriegel hat angeschlagen."}` };
  }
  if (k.rechnung === "uebersprungen") {
    return { ok: false, text: "Anzahlungsrechnung nicht ersetzt: die alte Zahlung ist inzwischen nicht mehr storniert. Es wurde nichts angefasst – bitte die Rechnungen prüfen." };
  }
  if (k.rechnung === "address_incomplete") {
    return { ok: false, text: "Die alte Anzahlungsrechnung ist storniert, die neue fehlt noch: die Rechnungsadresse ist unvollständig. Nach dem Ergänzen erneut „Plan anpassen“ klicken." };
  }
  if (k.rechnung === "wartet_auf_gutschrift") {
    return { ok: false, text: "Rechnung konnte nicht ersetzt werden: die Gutschrift zur alten Anzahlungsrechnung ist nicht entstanden. Erneut „Plan anpassen“ holt es nach; Details im Fehlerprotokoll." };
  }
  return { ok: false, text: `Rechnung konnte nicht ersetzt werden (${k.rechnung}). Erneut „Plan anpassen“ holt es nach; Details im Fehlerprotokoll.` };
}
